import React from "react";
import { Box, FormHelperText, Grid, IconButton, InputAdornment, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Icon from "src/components/icon";
import styled from "styled-components";
import Link from "src/components/link";
import { Helmet } from "react-helmet";
import TextField from "@mui/material/TextField";
import azure from "src/assets/icons/azure";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import request from "../../../utils/request";

const H1 = styled.h1`
    font-size: 22px;
    margin: 0;
    font-weight: normal;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const H2 = styled.h2`
    font-size: 14px;
    font-weight: normal;
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const P = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin-top: 28px;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const PermissionsIcon = styled(Icon)`
    background-color: #fff;
    border-radius: 16px;
    padding: 24px 12px 24px 12px;
    margin-top: 32px;
`;

const ContactPlaceholder =
    "In case of any inquiry regarding software permissions, please do not hesitate to ";

const PermissionsAzureOrg = () => {
    const [token, setToken] = React.useState({ value: undefined });
    const [orgs, setOrgs] = React.useState({ value: "" });
    const [showPassword, setShowPassword] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const navigate = useNavigate();

    const checkTokenValue = () => {
        if (!token.value || token.value.length < 5) {
            const tokenError = "Please enter a valid personal access token";
            setToken({ ...token, error: tokenError });
            return tokenError;
        }
        return undefined;
    };

    const checkOrgsValue = () => {
        if (!orgs.value || orgs.value.trim().length === 0) {
            const orgsError = "Please enter at least one organization name";
            setOrgs({ ...orgs, error: orgsError });
            return orgsError;
        }
        return undefined;
    };

    const handleSubmit = async (event) => {
        if (event) event.preventDefault();
        const tokenError = checkTokenValue();
        const orgsError = checkOrgsValue();
        if (tokenError || orgsError) return;

        const orgList = orgs.value
            .split(",")
            .map((o) => o.trim())
            .filter(Boolean);

        try {
            setLoading(true);
            await request("/api/azure/connect", {
                method: "POST",
                body: JSON.stringify({
                    token: token.value,
                    orgs: orgList,
                    centrally_managed: true,
                }),
            });
        } catch (e) {
            setLoading(false);
            if (e.status === 400) {
                setError(e.message);
                return;
            }
            navigate("/integrations/failure/azure");
            return;
        }
        setLoading(true);
        navigate("/integrations/success/azure");
    };

    return (
        <>
            <Helmet>
                <title>Adadot - Azure DevOps Permissions</title>
            </Helmet>
            <Grid container justifyContent="center" mb={4}>
                <Stack
                    direction="column"
                    alignItems="flex-start"
                    style={{ height: "100%", width: "70%" }}
                >
                    <form onSubmit={handleSubmit}>
                        <H1>Azure DevOps Integration (Organization)</H1>
                        <H2>Connect your admin PAT to scrape data for your entire organization</H2>
                        <PermissionsIcon svg={azure} width="100px" height="80px" />
                        <Box sx={{ width: 500, maxWidth: "100%", paddingTop: "2rem" }}>
                            <TextField
                                error={!!token.error}
                                value={token.value || ""}
                                helperText={token.error}
                                variant="outlined"
                                autoComplete="off"
                                type={showPassword ? "text" : "password"}
                                onChange={(e) =>
                                    setToken({ ...token, error: undefined, value: e.target.value })
                                }
                                onBlur={() => checkTokenValue()}
                                onFocus={() => setToken({ ...token, error: undefined })}
                                fullWidth
                                label="Personal Access Token"
                                id="pat-input"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle token visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ width: 500, maxWidth: "100%", paddingTop: "2rem" }}>
                            <TextField
                                error={!!orgs.error}
                                value={orgs.value}
                                helperText={
                                    orgs.error ||
                                    "Comma-separated list of Azure DevOps organization names (e.g. my-org, another-org)"
                                }
                                variant="outlined"
                                onChange={(e) =>
                                    setOrgs({ ...orgs, error: undefined, value: e.target.value })
                                }
                                onBlur={() => checkOrgsValue()}
                                onFocus={() => setOrgs({ ...orgs, error: undefined })}
                                fullWidth
                                label="Organization name(s)"
                                id="orgs-input"
                            />
                        </Box>
                        <P>
                            {ContactPlaceholder}
                            <Link to="/upgrade/inquiry">contact us</Link>
                        </P>
                        <Stack mt={4} direction="row" spacing={2}>
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                type="submit"
                                onClick={handleSubmit}
                                style={{ width: "102px" }}
                                loading={loading}
                            >
                                Continue
                            </LoadingButton>
                        </Stack>
                        <FormHelperText error>{error}</FormHelperText>
                    </form>
                </Stack>
            </Grid>
        </>
    );
};

export default PermissionsAzureOrg;
